/**
 * Licensed Materials - Property of IBM
 * IBM Business Analytics (C) Copyright IBM Corp. 2019
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */

/**
 * @class CallbackInvoker
 * @hideconstructor
 * @classdesc Class allowing to register a method associated to an id and invoke all the registered methods for a given id
 */
define([], () => {
	class CallbackInvoker {
		/**
		 * creates a callback invoker
		 * @param {Object} options - opions
		 * @param {Logger} options.logger - logger instance implementing warn, log, error method
		 */
		constructor(options) {
			this._logger = options.logger;
			this._callbacks = {};
		}

		/**
		 * registers a method for a given id; logs an error if no id is undefined or method is not a function
		 * @param {string} id - id to attach the method with
		 * @param {Function} method - function to register
		 */
		registerCallback(id, method) {
			if (typeof id !== 'string') {
				this._logger.error('undefined or invalid id when registering a method', id);
			} else if (typeof method !== 'function') {
				this._logger.error('undefined or invalid function when registering it', method);
			} else {
				if (typeof this._callbacks[id] === 'undefined') {
					this._callbacks[id] = [];
				}
				this._callbacks[id].push(method);
			}
		}

		/**
		 * removes a method for a given id; logs an error if no id is undefined or method is not a function
		 * @param {string} id - id to remove the method with
		 * @param {Function} method - previously attached function to remove
		 */
		removeCallback(id, method) {
			if (typeof id !== 'string') {
				this._logger.error('undefined or invalid id when removing a method', id);
			} else if (typeof this._callbacks[id] === 'undefined' ) {
				this._logger.info('no callback is registered with the provided id', id);
			} else if (typeof method !== 'function') {
				this._logger.error('undefined or invalid function when removing it', method);
			} else {
				let i=0;
				while (i < this._callbacks[id].length) {
					if (this._callbacks[id][i] === method) {
						this._callbacks[id].splice(i, 1);
					} else {
						i++;
					}
				}
			}
		}



		/**
		 * invokes all the methods registered with the given id; logs an error if the id is undefined or invalid
		 * @param {string} id - id to attach the method with
		 * @param {Object} [payload] - payload to pass
		 */
		invokeCallbacks(id, payload) {
			if (typeof id !== 'string') {
				this._logger.error('undefined or invalid id when invoking its associated methods', id);
			} else if (typeof this._callbacks[id] === 'undefined' ) {
				this._logger.info('no callback is registered with the provided id', id);
			} else {
				let thisValue = this;
				this._callbacks[id].forEach((method) => {
					try {
						method(payload);
					} catch(error) {
						thisValue._logger.error('error thrown when invoking registered method', error);
					}
				});
			}
		}
	}

	return CallbackInvoker;
});
